import { userApi } from '../api/user.api';

const CACHE_KEY = 'pj_menu_preferences_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour - "timeout of menu update should be 1 hour, not earlier"

// Sidebar.jsx calls this instead of userApi.getMenuPreferences() directly -
// localStorage (not just in-memory state) so the 1-hour window survives a
// page reload/new tab too, not just staying within one mounted session.
export async function getMenuPreferencesCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        return cached.data;
      }
    }
  } catch { /* corrupt cache entry - fall through and refetch */ }

  const res = await userApi.getMenuPreferences();
  const data = res.data || { items: [], num_of_tab: null };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data }));
  } catch { /* localStorage full/unavailable - not fatal, just no caching */ }
  return data;
}

// Called right after MenuOrderPage.jsx saves a change - without this the
// sidebar would keep showing the stale order/hide state for up to an hour
// even though the user just edited it themselves.
export function invalidateMenuPreferencesCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}

// Applies saved order/hide state to the normalized top-level menu array
// Sidebar.jsx builds from get_menu_item. main_menu_index is this item's
// position in `items` BEFORE any reordering - stable as long as the
// backend's menu shape for this role doesn't change, same assumption the
// backend side documents (see saveMenuPreferences in views.py).
//
// Returns {visible, hidden} - hidden items are rendered separately behind
// the sidebar's "+" toggle instead of being dropped entirely.
export function applyMenuPreferences(items, preferences) {
  const prefItems = preferences?.items || [];
  if (prefItems.length === 0) return { visible: items, hidden: [] };

  const byIndex = new Map(prefItems.map((p) => [p.main_menu_index, p]));
  const withMeta = items.map((item, i) => {
    const pref = byIndex.get(i);
    return { item, order: pref ? pref.new_menu_index : i, hide: pref?.hide || false };
  });
  withMeta.sort((a, b) => a.order - b.order);

  return {
    visible: withMeta.filter((m) => !m.hide).map((m) => m.item),
    hidden: withMeta.filter((m) => m.hide).map((m) => m.item),
  };
}
